/**
 * Editor-side i18n core (spec #304): German or English per editor, picked from
 * the company DB's `people.language` (the roster sync stores it as
 * `editors.languages`, served on `GET /api/editor/me`). No manual toggle, no
 * framework: typed catalogs per screen group in `./messages/`, merged in
 * `./index.tsx`. Client screens and admin stay English and do not use this.
 *
 * @module web/i18n/core
 */

export type Locale = "en" | "de";

export type Vars = Record<string, string | number>;

/** `de` when the editor's language is German, otherwise English (empty -> English). */
export function localeFromLanguages(languages: readonly string[] | null | undefined): Locale {
  return (languages ?? []).some((l) => typeof l === "string" && l.trim().toLowerCase().startsWith("de")) ? "de" : "en";
}

/** `{name}` placeholders; an unknown placeholder stays visible so a test catches it. */
export function interpolate(template: string, vars?: Vars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

const NUMBER_LOCALE: Record<Locale, string> = { en: "en-IE", de: "de-DE" };

/** The `Intl` locale tag for dates and numbers. */
export function intlLocale(locale: Locale): string {
  return NUMBER_LOCALE[locale];
}

/**
 * Euros per language: de "68 €", "1.281 €", "12,50 €"; en "€68", "€1,281", "€12.50".
 * `decimals: "auto"` (default) drops ".00" on whole amounts; `2` always shows cents.
 */
export function formatEur(cents: number, locale: Locale, opts: { decimals?: "auto" | 2 } = {}): string {
  const decimals = opts.decimals ?? "auto";
  const minimumFractionDigits = decimals === 2 ? 2 : cents % 100 ? 2 : 0;
  const sign = cents < 0 ? "-" : "";
  const n = (Math.abs(cents) / 100).toLocaleString(NUMBER_LOCALE[locale], { minimumFractionDigits, maximumFractionDigits: 2 });
  return locale === "de" ? `${sign}${n} €` : `${sign}€${n}`;
}

export function formatNumber(n: number, locale: Locale): string {
  return n.toLocaleString(NUMBER_LOCALE[locale]);
}

export function formatDate(iso: string | Date, locale: Locale, opts: Intl.DateTimeFormatOptions): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  return d.toLocaleDateString(NUMBER_LOCALE[locale], opts);
}

export function formatDateTime(iso: string | Date, locale: Locale, opts: Intl.DateTimeFormatOptions): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  return d.toLocaleString(NUMBER_LOCALE[locale], opts);
}

/**
 * Typed pair of catalogs for one screen group: German must carry exactly the
 * English keys (a missing or extra key is a type error in `npm run build`,
 * and `i18n.test.ts` checks the same at runtime).
 */
export function defineMessages<K extends string>(en: Record<K, string>, de: Record<K, string>): { en: Record<K, string>; de: Record<K, string> } {
  return { en, de };
}
